import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCsrf } from '../utils/csrf';

interface PreviewData {
  total: number;
  read: number;
  currently_reading: number;
  to_read: number;
  sample_books: Array<{
    title: string;
    author: string;
    rating: number | null;
    date_read: string | null;
  }>;
}

interface ImportResult {
  total: number;
  imported: number;
  failed: number;
  failed_books: Array<{
    title: string;
    author: string;
    error: string;
  }>;
}

export default function CsvImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
      setError(null);
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    setPreviewing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetchWithCsrf('/api/v1/csv_imports/preview', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setPreview(data.preview);
      } else {
        setError(data.error || 'Failed to preview CSV');
      }
    } catch (err) {
      setError('Failed to preview CSV file');
      console.error(err);
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetchWithCsrf('/api/v1/csv_imports', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data.result);
        setPreview(null);
        setFile(null);
      } else {
        setError(data.error || 'Failed to import CSV');
      }
    } catch (err) {
      setError('Failed to import CSV file');
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Import Reading History</h1>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3">How to Import from Goodreads</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-700">
          <li>Go to <a href="https://www.goodreads.com/review/import" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Goodreads Export</a></li>
          <li>Click "Export Library" and download your CSV file</li>
          <li>Upload the CSV file below</li>
          <li>Preview your books and confirm the import</li>
        </ol>
        <p className="mt-3 text-sm text-gray-600">
          We'll import your books marked as "read", "currently-reading", and "to-read", along with your ratings and reviews.
        </p>
      </div>

      {/* File Upload */}
      {!result && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Upload CSV File</h2>

          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="flex-1 border border-gray-300 rounded px-3 py-2"
            />

            {file && !preview && (
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {previewing ? 'Loading...' : 'Preview'}
              </button>
            )}
          </div>

          {file && (
            <p className="mt-2 text-sm text-gray-600">
              Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Preview */}
      {preview && !result && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Preview</h2>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-blue-600">{preview.total}</div>
              <div className="text-sm text-gray-600">Total Books</div>
            </div>
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-green-600">{preview.read}</div>
              <div className="text-sm text-gray-600">Read</div>
            </div>
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-yellow-600">{preview.currently_reading}</div>
              <div className="text-sm text-gray-600">Currently Reading</div>
            </div>
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-purple-600">{preview.to_read}</div>
              <div className="text-sm text-gray-600">To Read</div>
            </div>
          </div>

          {preview.sample_books.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Sample Books:</h3>
              <div className="space-y-2">
                {preview.sample_books.map((book, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded">
                    <div className="font-medium">{book.title}</div>
                    <div className="text-sm text-gray-600">{book.author}</div>
                    {book.rating && book.rating > 0 && (
                      <div className="text-sm text-yellow-600">
                        {'★'.repeat(book.rating)}{'☆'.repeat(5 - book.rating)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleImport}
              disabled={importing}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 font-semibold"
            >
              {importing ? 'Importing...' : 'Confirm Import'}
            </button>
            <button
              onClick={handleReset}
              disabled={importing}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:bg-gray-200"
            >
              Cancel
            </button>
          </div>

          {importing && (
            <div className="mt-4 text-center text-gray-600">
              <p>This may take a few minutes for large libraries...</p>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Import Complete!</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-blue-600">{result.total}</div>
              <div className="text-sm text-gray-600">Total Books</div>
            </div>
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-green-600">{result.imported}</div>
              <div className="text-sm text-gray-600">Imported</div>
            </div>
            <div className="bg-gray-50 p-4 rounded text-center">
              <div className="text-3xl font-bold text-red-600">{result.failed}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          {result.failed > 0 && result.failed_books.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3 text-red-800">Failed Imports:</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {result.failed_books.map((book, index) => (
                  <div key={index} className="p-3 bg-red-50 rounded">
                    <div className="font-medium">{book.title}</div>
                    <div className="text-sm text-gray-600">{book.author}</div>
                    <div className="text-sm text-red-600">{book.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded hover:bg-indigo-700 font-semibold"
            >
              Back to Dashboard
            </button>
            <button
              onClick={handleReset}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-semibold"
            >
              Import Another File
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
